/**
 * Landing card row + in-flow detail panel (accordion-style).
 * On stacked/mobile layouts, detail opens directly under the selected card.
 */

import { Fragment, useCallback, useEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';

import { useLandingExpandableCards } from '@hooks/useLandingExpandableCards';
import { useStackedCardDeckLayout } from '@hooks/useStackedCardDeckLayout';

/** Scroll so the detail panel sits below the sticky landing header with breathing room. */
function scrollDetailBelowHeader(el: HTMLElement, comfortPx: number): void {
  const header = document.querySelector('.bhg-landing-header');
  const headerBottom = header?.getBoundingClientRect().bottom ?? 88;
  const y = el.getBoundingClientRect().top + window.scrollY - headerBottom - comfortPx;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: Math.max(0, y), behavior: reducedMotion ? 'auto' : 'smooth' });
}

export interface LandingCardDeckProps<T extends string> {
  items: readonly { id: T }[];
  rowClassName: string;
  sectionRef?: RefObject<HTMLElement | null>;
  detailPanelId?: string;
  /**
   * When viewport width is at or below this value, detail opens under the tapped card
   * instead of below the full row (pillars stack at 1024px; mission at 639px).
   */
  inlineDetailMaxWidth?: number;
  renderCard: (
    item: { id: T },
    ctx: {
      isSelected: boolean;
      isDimmed: boolean;
      onSelect: () => void;
      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    }
  ) => ReactNode;
  renderDetail: (item: { id: T }, ctx: { onClose: () => void }) => ReactNode;
}

function DetailPanel<T extends string>({
  expandedItem,
  detailPanelId,
  detailRef,
  placement,
  renderDetail,
  onClose,
}: {
  expandedItem: { id: T };
  detailPanelId: string;
  detailRef?: RefObject<HTMLDivElement | null>;
  placement: 'inline' | 'below-row';
  renderDetail: (item: { id: T }, ctx: { onClose: () => void }) => ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      ref={detailRef}
      className={[
        'bhg-landing-card-deck__detail',
        'bhg-landing-card-deck__detail--open',
        placement === 'inline'
          ? 'bhg-landing-card-deck__detail--inline'
          : 'bhg-landing-card-deck__detail--below-row',
      ].join(' ')}
    >
      <div className="bhg-landing-card-deck__detail-inner">
        <div
          id={detailPanelId}
          key={expandedItem.id}
          className="bhg-landing-card-deck__detail-panel"
          role="region"
          aria-live="polite"
        >
          {renderDetail(expandedItem, { onClose })}
        </div>
      </div>
    </div>
  );
}

export function LandingCardDeck<T extends string>({
  items,
  rowClassName,
  sectionRef,
  detailPanelId = 'landing-card-detail',
  inlineDetailMaxWidth = 1024,
  renderCard,
  renderDetail,
}: LandingCardDeckProps<T>) {
  const { expandedKey, toggle, collapse, hasExpanded } = useLandingExpandableCards<T>(sectionRef);
  const stackedLayout = useStackedCardDeckLayout(inlineDetailMaxWidth);
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const cardSlotRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  /**
   * Close the detail panel and scroll the browser back up so the card that
   * was tapped is visible again (below the sticky header). Two rAF ticks give
   * React time to re-render and the browser time to recalculate layout after
   * the panel collapses before we read the card's position.
   */
  const handleClose = useCallback(() => {
    const targetEl = expandedKey ? cardSlotRefs.current.get(expandedKey) : null;
    collapse();
    if (!targetEl) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollDetailBelowHeader(targetEl, 24);
      });
    });
  }, [expandedKey, collapse]);

  const expandedItem = items.find((item) => item.id === expandedKey);

  useEffect(() => {
    if (!expandedKey || !detailScrollRef.current) {
      return;
    }

    const el = detailScrollRef.current;
    const comfortPx = stackedLayout ? 32 : 16;

    const runScroll = () => scrollDetailBelowHeader(el, comfortPx);

    if (!stackedLayout) {
      runScroll();
      return;
    }

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'grid-template-rows') {
        runScroll();
      }
    };

    el.addEventListener('transitionend', onTransitionEnd);
    const fallback = window.setTimeout(runScroll, 480);

    return () => {
      el.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(fallback);
    };
  }, [expandedKey, stackedLayout]);

  return (
    <div className={`bhg-landing-card-deck${hasExpanded ? ' bhg-landing-card-deck--open' : ''}`}>
      <div className={`bhg-landing-card-deck__row ${rowClassName}`}>
        {items.map((item) => {
          const isSelected = expandedKey === item.id;
          const isDimmed = hasExpanded && !isSelected;

          const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggle(item.id);
            }
          };

          return (
            <Fragment key={item.id}>
              <div
                className="bhg-landing-card-deck__slot"
                ref={(el) => {
                  if (el) cardSlotRefs.current.set(item.id, el);
                  else cardSlotRefs.current.delete(item.id);
                }}
              >
                {renderCard(item, {
                  isSelected,
                  isDimmed,
                  onSelect: () => toggle(item.id),
                  onKeyDown,
                })}
              </div>
              {stackedLayout && isSelected && expandedItem ? (
                <DetailPanel
                  expandedItem={expandedItem}
                  detailPanelId={detailPanelId}
                  detailRef={detailScrollRef}
                  placement="inline"
                  renderDetail={renderDetail}
                  onClose={handleClose}
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>

      {!stackedLayout && hasExpanded && expandedItem ? (
        <DetailPanel
          expandedItem={expandedItem}
          detailPanelId={detailPanelId}
          detailRef={detailScrollRef}
          placement="below-row"
          renderDetail={renderDetail}
          onClose={handleClose}
        />
      ) : null}
    </div>
  );
}
